/**
 * Core model type definitions for the museum application
 * These types reflect the database schema and API responses
 */

/**
 * Base model interface with common properties
 */
export interface BaseModel {
    id: number;
    created_at: string;
    updated_at: string;
}

/**
 * Language model
 */
export interface Language extends BaseModel {
    code: string;
    name: string;
    native_name: string;
    is_default: boolean;
    is_active: boolean;
    direction: 'ltr' | 'rtl';
}

/**
 * Room status enum
 */
export enum RoomStatus {
    Draft = 'draft',
    Published = 'published',
    Archived = 'archived',
}

/**
 * Room model
 */
export interface Room extends BaseModel {
    status: RoomStatus;
    map_position_x: number | null;
    map_position_y: number | null;
    translations: RoomTranslation[];
    exhibits: Exhibit[];
    thumbnail_url: string | null;
    video_url: string | null;
    gallery_urls: string[] | null;
    panorama_url: string | null;
    audio_guide_url: string | null;
}

/**
 * Room translation model
 */
export interface RoomTranslation {
    id: number;
    room_id: number;
    language_id: number;
    language_code: string; // Convenience field
    name: string;
    description: string | null;
}

/**
 * Exhibit status enum
 */
export enum ExhibitStatus {
    Draft = 'draft',
    Published = 'published',
    Archived = 'archived',
    OnDisplay = 'on_display',
}

/**
 * Exhibit model
 */
export interface Exhibit extends BaseModel {
    artist: string | null;
    year_created: string | null;
    dimensions: string | null;
    acquisition_date: string | null;
    value: number | null;
    status: ExhibitStatus;
    thumbnail_url: string | null;
    audio_guide_url: string | null;
    video_url: string | null;
    model_url: string | null;
    qr_code_url: string | null;
    default_language: string;
    translations: ExhibitTranslation[];
    rooms: Room[];
    gallery_urls: string[] | null;
    documents_urls: string[] | null;
}

/**
 * Exhibit translation model
 */
export interface ExhibitTranslation {
    id: number;
    exhibit_id: number;
    language_id: number;
    language_code: string; // Convenience field
    title: string;
    description: string | null;
    medium: string | null; // Added to match the PHP model's getMedium() method
}

/**
 * Media type enum
 */
export enum MediaType {
    Image = 'image',
    Video = 'video',
    Audio = 'audio',
    Panorama = 'panorama',
    Model3D = 'model_3d',
    Document = 'document',
}

/**
 * Base media interface
 */
export interface BaseMedia extends BaseModel {
    type: MediaType;
    file_path: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    is_featured: boolean;
    display_order: number;
}

/**
 * Room media model
 */
export interface RoomMedia extends BaseMedia {
    room_id: number;
    translations: RoomMediaTranslation[];
}

/**
 * Room media translation model
 */
export interface RoomMediaTranslation {
    id: number;
    room_media_id: number;
    language_id: number;
    language_code: string; // Convenience field
    title: string | null;
    caption: string | null;
    alt_text: string | null;
}

/**
 * Exhibit media model
 */
export interface ExhibitMedia extends BaseMedia {
    exhibit_id: number;
    translations: ExhibitMediaTranslation[];
}

/**
 * Exhibit media translation model
 */
export interface ExhibitMediaTranslation {
    language_code: string; // Convenience field
    alt_text: string | null;
    title: string | null;
    description: string | null;
    caption: string | null;
    file_path: string | null;
}

/**
 * API-formatted exhibit media item
 */
export interface ExhibitMediaItem {
    id: number;
    title: string | null;
    description: string | null;
    file_path: string;
    media_type: string;
    order_position: number;
    translations: ExhibitMediaTranslation[];
}

/**
 * API-formatted exhibit media structure
 */
export interface ExhibitMediaCollection {
    photos: ExhibitMediaItem[];
    videos: ExhibitMediaItem[];
    audio_guides: ExhibitMediaItem[];
    models: ExhibitMediaItem[];
    documents: ExhibitMediaItem[];
}

/**
 * API-formatted room media structure
 */
export interface RoomMediaCollection {
    panoramas: RoomMediaItem[];
    photos: RoomMediaItem[];
    videos: RoomMediaItem[];
    audio_guides: RoomMediaItem[];
}

/**
 * API-formatted room media item
 */
export interface RoomMediaItem {
    id: number;
    title: string | null;
    description: string | null;
    file_path: string;
    media_type: string;
    order_position: number;
    translations: RoomMediaTranslation[];
}

/**
 * API response interfaces
 */
export interface PaginatedResponse<T> {
    data: T[];
    meta: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
    };
    links: {
        first: string;
        last: string;
        next: string | null;
        prev: string | null;
    };
}

/**
 * Search filters
 */
export interface SearchFilters {
    query?: string;
    status?: string;
    language?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
}
